import { motion } from "framer-motion";
import { Factory, HeartPulse, Server, BatteryCharging } from "lucide-react";
import { Box, Typography } from "@mui/material";
// import { useSEO } from "@/lib/seo";
// import { Reveal } from "@/components/site/Reveal";
// import { CTASection } from "@/components/site/CTASection";
import { useSEO } from "../lib/seo";
import { Reveal } from "../site/Reveal";
import { CTASection } from "../site/CTASection";

// NOTE: Replace these with your actual design-token values (Tailwind config
// or theme.palette equivalents) so colors match exactly.
const COLORS = {
  obsidian: "#0B0B0C",
  ink: "#1A1A1A",
  safety: "#FF6A00",
  concrete: "#F2F1EE",
  white: "#FFFFFF",
  mutedForeground: "#6B7280",
};
const colors = {
  obsidian: "var(--obsidian, #0a0a0a)",
  safety: "var(--safety, #ff6b35)",
  ink: "var(--ink, #111111)",
  mutedForeground: "var(--muted-foreground, #6b7280)",
  white60: "rgba(255,255,255,0.6)",
  inkBorder15: "rgba(17,17,17,0.15)",
};

const fontDisplay = "var(--font-display, inherit)";
const fontMono = "var(--font-mono, monospace)";
const INDUSTRIES = [
  {
    icon: Factory,
    t: "Manufacturing & Heavy Machinery",
    d: "Protecting motors, compressors, CNC machines and other high starting-current loads from voltage instability.",
  },
  {
    icon: HeartPulse,
    t: "Healthcare & Medical Equipment",
    d: "Clean, isolated power for imaging systems, diagnostic and life-critical medical electronics.",
  },
  {
    icon: Server,
    t: "Office Automation & IT Infrastructure",
    d: "Stable, noise-free supply for servers, data centres and sensitive office automation equipment.",
  },
  {
    icon: BatteryCharging,
    t: "Generators & Backup Power",
    d: "Automatic operation on both generator and mains supply for seamless power continuity.",
  },
];

export default function Industries() {
  useSEO({
    title: "Industries Served | ITL Technical Services",
    description:
      "ITL Technical Services powers manufacturing, healthcare, IT infrastructure and backup power systems with reliable voltage stabilizers, isolation transformers and CVTs.",
  });

  return (
    <>
      {/* Header */}
      <Box
        data-testid="industries-header"
        className="grain grain-dark"
        sx={{
          position: "relative",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 16, md: 22 },
          pb: { xs: 8, md: 12 },

          backgroundImage: `
      linear-gradient(
        rgba(10,10,10,0.75),
        rgba(10,10,10,0.82)
      ),
      url("/IndustriesBanner.jpg")
    `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            maxWidth: "1280px", // max-w-7xl
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          <Typography
            className="mono-label"
            sx={{
              fontFamily: fontMono,
              color: colors.safety,
              mb: 4, // mb-8
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontSize: "0.75rem",
              textAlign: "left",
            }}
          >
            [ Industries served ]
          </Typography>

          <Typography
            component={motion.h1}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
              fontWeight: 900,
              fontSize: { xs: "3rem", md: "4.5rem" }, // text-5xl -> md:text-7xl
              letterSpacing: "-0.04em", // tracking-tighter
              lineHeight: 0.85,
              maxWidth: "56rem", // max-w-4xl
              textAlign: "left",
            }}
          >
            Where reliable power matters most.
          </Typography>
        </Box>
      </Box>

      {/* Industries grid */}
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 8, md: 12 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1px",
            backgroundColor: "rgba(26,26,26,0.15)", // bg-ink/15
            border: "1px solid rgba(26,26,26,0.15)", // border-ink/15
          }}
        >
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.t} delay={i * 0.08} className="bg-white">
              <Box
                sx={{
                  backgroundColor: COLORS.white,
                  p: { xs: 4, md: 6 }, // p-8 / md:p-12
                  height: "100%",
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: COLORS.concrete,
                  },
                }}
              >
                <Box
                  component={ind.icon}
                  sx={{
                    width: 40,
                    height: 40,
                    color: COLORS.safety,
                    mb: 4,
                    display: "block",
                    mr: "auto",
                  }}
                  strokeWidth={1.5}
                />

                <Typography
                  component="h2"
                  sx={{
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    fontWeight: 900,
                    fontSize: { xs: "1.5rem", md: "1.875rem" }, // text-2xl -> md:text-3xl
                    letterSpacing: "-0.04em",
                    color: COLORS.ink,
                    textAlign: "left",
                  }}
                >
                  {ind.t}
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.mutedForeground,
                    mt: 2,
                    lineHeight: 1.7,
                    textAlign: "left",
                  }}
                >
                  {ind.d}
                </Typography>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Box>

      <CTASection />
    </>
  );
}
